import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import http from "../../utils/axios";

// ─── Thunks ──────────────────────────────────────────────────────────────────

/** Overview: transactions + business + customer stats for a period */
export const getAdminDashboard = createAsyncThunk(
  "adminReport/getAdminDashboard",
  async (period = "month", { rejectWithValue }) => {
    try {
      const res = await http.get(`/dashboard/reports/admin/dashboard?period=${period}`);
      return res;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to fetch admin dashboard");
    }
  }
);

/** Overview: commission summary for a period */
export const getAdminCommission = createAsyncThunk(
  "adminReport/getAdminCommission",
  async (period = "month", { rejectWithValue }) => {
    try {
      const res = await http.get(`/dashboard/reports/admin/commission?period=${period}`);
      return res;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to fetch commission report");
    }
  }
);

/** Settlements: paginated per-business commission list */
export const getCommissionPayments = createAsyncThunk(
  "adminReport/getCommissionPayments",
  async ({ month, year, status = "all", search = "", per_page = 15, page = 1 } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (month)    params.append("month", month);
      if (year)     params.append("year", year);
      if (status)   params.append("status", status);
      if (search)   params.append("search", search);
      if (per_page) params.append("per_page", per_page);
      if (page)     params.append("page", page);
      const res = await http.get(`/dashboard/commission-payments?${params.toString()}`);
      return res;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to fetch commission payments");
    }
  }
);

/** Settlements: high-level totals for a month */
export const getCommissionSummary = createAsyncThunk(
  "adminReport/getCommissionSummary",
  async ({ month, year } = {}, { rejectWithValue }) => {
    try {
      const params = new URLSearchParams();
      if (month) params.append("month", month);
      if (year)  params.append("year", year);
      const res = await http.get(`/dashboard/commission-payments/summary?${params.toString()}`);
      return res;
    } catch (err) {
      return rejectWithValue(err.response?.data || "Failed to fetch commission summary");
    }
  }
);

/** Toggle paid/unpaid for a specific business-month record */
export const toggleCommissionPayment = createAsyncThunk(
  "adminReport/toggleCommissionPayment",
  async ({ uuid, is_paid }, { rejectWithValue }) => {
    try {
      const res = await http.patch(
        `/dashboard/commission-payments/${uuid}/toggle-payment-status`,
        { is_paid }
      );
      return { ...res, uuid, is_paid };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || "Failed to update payment status");
    }
  }
);

// ─── Slice ───────────────────────────────────────────────────────────────────

const adminReportSlice = createSlice({
  name: "adminReport",
  initialState: {
    // Overview tab
    dashboardData: null,
    dashboardLoading: false,
    dashboardError: null,
    commissionData: null,
    commissionLoading: false,
    commissionError: null,

    // Settlements tab
    payments: [],
    paymentsPagination: null,
    paymentsFilter: null,
    paymentsLoading: false,
    paymentsError: null,

    summary: null,
    summaryLoading: false,
    summaryError: null,

    // Per-row toggle loading: { [uuid]: boolean }
    toggleLoadingMap: {},
    toggleError: null,
  },
  reducers: {
    clearToggleError(state) {
      state.toggleError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // ── getAdminDashboard ──────────────────────────────────────────────
      .addCase(getAdminDashboard.pending, (state) => {
        state.dashboardLoading = true;
        state.dashboardError = null;
      })
      .addCase(getAdminDashboard.fulfilled, (state, action) => {
        state.dashboardLoading = false;
        state.dashboardData = action.payload?.data || action.payload;
      })
      .addCase(getAdminDashboard.rejected, (state, action) => {
        state.dashboardLoading = false;
        state.dashboardError = action.payload;
      })

      // ── getAdminCommission ─────────────────────────────────────────────
      .addCase(getAdminCommission.pending, (state) => {
        state.commissionLoading = true;
        state.commissionError = null;
      })
      .addCase(getAdminCommission.fulfilled, (state, action) => {
        state.commissionLoading = false;
        state.commissionData = action.payload?.data || action.payload;
      })
      .addCase(getAdminCommission.rejected, (state, action) => {
        state.commissionLoading = false;
        state.commissionError = action.payload;
      })

      // ── getCommissionPayments ──────────────────────────────────────────
      .addCase(getCommissionPayments.pending, (state) => {
        state.paymentsLoading = true;
        state.paymentsError = null;
      })
      .addCase(getCommissionPayments.fulfilled, (state, action) => {
        state.paymentsLoading = false;
        const payload = action.payload?.data || action.payload;
        state.payments = payload?.data || [];
        state.paymentsPagination = payload?.pagination || null;
        state.paymentsFilter = payload?.filter || null;
      })
      .addCase(getCommissionPayments.rejected, (state, action) => {
        state.paymentsLoading = false;
        state.paymentsError = action.payload;
        state.payments = [];
      })

      // ── getCommissionSummary ───────────────────────────────────────────
      .addCase(getCommissionSummary.pending, (state) => {
        state.summaryLoading = true;
        state.summaryError = null;
      })
      .addCase(getCommissionSummary.fulfilled, (state, action) => {
        state.summaryLoading = false;
        state.summary = action.payload?.data || action.payload;
      })
      .addCase(getCommissionSummary.rejected, (state, action) => {
        state.summaryLoading = false;
        state.summaryError = action.payload;
      })

      // ── toggleCommissionPayment ────────────────────────────────────────
      .addCase(toggleCommissionPayment.pending, (state, action) => {
        const { uuid } = action.meta.arg;
        state.toggleLoadingMap[uuid] = true;
        state.toggleError = null;
      })
      .addCase(toggleCommissionPayment.fulfilled, (state, action) => {
        const { uuid, is_paid } = action.payload;
        state.toggleLoadingMap[uuid] = false;
        // Optimistically update the row in the payments list
        const row = state.payments.find((p) => p.settlement?.uuid === uuid);
        if (row && row.settlement) {
          row.settlement.is_paid = is_paid;
          row.settlement.status = is_paid ? "Paid" : "Unpaid";
          row.settlement.paid_at = action.payload?.data?.settlement?.paid_at || null;
        }
      })
      .addCase(toggleCommissionPayment.rejected, (state, action) => {
        const { uuid } = action.meta.arg;
        state.toggleLoadingMap[uuid] = false;
        state.toggleError = action.payload;
      });
  },
});

export const { clearToggleError } = adminReportSlice.actions;
export default adminReportSlice.reducer;
